import { formatLocalDate, localDateInputValue } from '../utils/localDateTime';
import { showAppAlert } from '../common/appDialog';
import { clientWorkspaceStorage } from '../common/organizationAccessMode';

import React, { useState, useEffect, useMemo } from 'react';
import { ICONS } from './constants';
import type { Student, EnrolledCourse, TrainerProfile, AssessmentSummary, Organization } from '@types';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';
import { generateGenericPdf, generateCertificatePdf } from '../utils/pdfGenerator';
import { getCertificateSignatories } from '../utils/learnerCertificate';
import LearnerCertificate from './LearnerCertificate';
import type AnalysisFormProps from './AnalysisForm';
import { LMS_LEVELS, MODULE_DATA } from '../../services/lmsData';
import { LmsRoleSelection } from './LmsAuth';
import { generateCustomLmsModule, generateCompetencyPathwayFromObjective, evaluateSecurityResponse, evaluateShortAnswer, generateLearnerReport, generateLessonPlan, generateCareerPathway } from '@common/services/geminiService';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import Loader from './Loader';
import AnalysisResultDisplay from './AnalysisResultDisplay';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const md = createSafeMarkdown();

// --- Types ---
interface LessonPlan {
    id: string;
    title: string;
    content: string;
    moduleId: string;
    createdAt: string;
}

interface CourseOption {
    id: string;
    title: string;
    type: 'Standard' | 'Custom Course' | 'Custom Module';
    modules: string[];
}

const normalizeModuleReference = (value: unknown) => String(value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const normalizeModuleSubject = (value: unknown) => normalizeModuleReference(value)
    .replace(/^(?:l\d+ add \d+|module \d+)\s+/, '');

const resolveModule = (reference: unknown, customModules: any[] = []) => {
    const referenceObject = reference && typeof reference === 'object' ? reference as any : null;
    if (referenceObject?.content || referenceObject?.lessonContent)
        return { id: referenceObject.id || referenceObject.moduleId || referenceObject.title || 'custom-module', data: referenceObject };
    const directId = String(referenceObject?.id || referenceObject?.moduleId || referenceObject?.code || referenceObject?.title || referenceObject?.name || reference || '').trim();
    if (MODULE_DATA[directId])
        return { id: directId, data: MODULE_DATA[directId] };
    const embeddedId = directId.match(/CPPSEC[A-Z]*\d+/i)?.[0].toUpperCase();
    if (embeddedId && MODULE_DATA[embeddedId])
        return { id: embeddedId, data: MODULE_DATA[embeddedId] };

    const normalized = normalizeModuleReference(directId);
    const subject = normalizeModuleSubject(directId);
    for (const level of Object.values(LMS_LEVELS)) {
        for (const module of level.modules) {
            if (normalizeModuleReference(module.id) === normalized || normalizeModuleReference(module.title) === normalized)
                return { id: module.id, data: MODULE_DATA[module.id] };
        }
    }
    for (const [id, data] of Object.entries(MODULE_DATA))
        if (normalizeModuleReference(data.title) === normalized || normalizeModuleSubject(data.title) === subject)
            return { id, data };

    const custom = customModules.find(module =>
        normalizeModuleReference(module.id) === normalized || normalizeModuleReference(module.title) === normalized);
    return custom ? { id: custom.id, data: custom } : null;
};

// --- Sub-components ---

import { getFunctions, httpsCallable } from 'firebase/functions';

// Simple in-memory cache to avoid recompiling audio on every playback
const audioCache: Record<string, string> = {};

const AiInstructorCard: React.FC<{ text: string; title?: string; avatarUrl?: string }> = ({ text, title = "Chief Instructor", avatarUrl = "/ai_instructor.png" }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
    
    useEffect(() => {
        return () => {
            if (audioElement) {
                audioElement.pause();
                audioElement.src = "";
            }
        };
    }, [audioElement]);

    const handlePlayPause = async () => {
        if (!text) return;

        if (isPlaying) {
            window.speechSynthesis.cancel();
            setIsPlaying(false);
            return;
        }

        setIsLoadingAudio(true);
        const cleanText = text.replace(/<[^>]*>?/gm, '');
        
        try {
            const utterance = new SpeechSynthesisUtterance(cleanText);
            
            // Try to select a professional-sounding voice
            const voices = window.speechSynthesis.getVoices();
            const preferredVoice = voices.find(v => (v.name.includes('Google') || v.name.includes('Eng')) && (v.lang.includes('US') || v.lang.includes('UK'))) || voices[0];
            if (preferredVoice) utterance.voice = preferredVoice;

            utterance.rate = 1.0;
            utterance.pitch = 0.95;

            utterance.onend = () => setIsPlaying(false);
            utterance.onerror = () => {
                console.error("Native synthesis failed.");
                setIsPlaying(false);
            };

            window.speechSynthesis.speak(utterance);
            setIsPlaying(true);
        } catch (error) {
            console.error("Failed to play native TTS audio:", error);
            void showAppAlert("Audio playback failed. Please check device volume/settings.");
            setIsPlaying(false);
        } finally {
            setIsLoadingAudio(false);
        }
    };

    return (
        <div className="bg-slate-900 rounded-xl overflow-hidden shadow-lg border border-slate-700 flex flex-col md:flex-row mb-8">
            <div className="w-full md:w-48 bg-slate-800 relative flex items-center justify-center p-4">
                {/* Simulated Video Placeholder */}
                <div className="absolute inset-0 opacity-40 mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"4\" height=\"4\" viewBox=\"0 0 4 4\"%3E%3Cpath fill=\"%23ffffff\" fill-opacity=\"0.1\" d=\"M1 3h1v1H1V3zm2-2h1v1H3V1z\"%3E%3C/path%3E%3C/svg%3E')" }}></div>
                <div className={`w-32 h-32 rounded-full border-4 border-slate-600 bg-slate-700 flex items-center justify-center shadow-inner relative overflow-hidden transition-all duration-300 ${isPlaying ? 'ring-4 ring-blue-500/50 scale-105 shadow-[0_0_15px_rgba(59,130,246,0.5)]' : ''}`}>
                    <img src={avatarUrl} alt="AI Instructor" className="w-full h-full object-cover" />
                    {isPlaying && (
                        <div className="absolute inset-x-0 bottom-0 top-0 flex items-end justify-center pb-2 bg-gradient-to-t from-black/80 to-transparent">
                            <div className="flex gap-1 opacity-90 items-end">
                                <div className="w-1.5 h-4 bg-blue-400 rounded-full animate-[bounce_1s_infinite]" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-1.5 h-6 bg-blue-400 rounded-full animate-[bounce_1s_infinite]" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-1.5 h-3 bg-blue-400 rounded-full animate-[bounce_1s_infinite]" style={{ animationDelay: '300ms' }}></div>
                                <div className="w-1.5 h-5 bg-blue-400 rounded-full animate-[bounce_1s_infinite]" style={{ animationDelay: '200ms' }}></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="p-6 flex-1 flex flex-col justify-center">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h4 className="text-white font-bold text-lg">{title}</h4>
                        <p className="text-slate-400 text-sm">AI Audio Briefing</p>
                    </div>
                </div>
                <button 
                    onClick={handlePlayPause}
                    disabled={isLoadingAudio}
                    className={`flex items-center justify-center py-3 px-6 rounded-lg font-bold shadow-md transition-all w-full md:w-auto ${isLoadingAudio ? 'bg-slate-700 text-slate-400 cursor-wait border-transparent' : (isPlaying ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/50' : 'bg-blue-600 text-white hover:bg-blue-700 border-transparent')}`}
                >
                    {isLoadingAudio ? (
                        <>
                            <div className="w-5 h-5 border-2 border-slate-400 rounded-full border-t-transparent animate-spin mr-2"></div>
                            Receiving Uplink...
                        </>
                    ) : isPlaying ? (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Stop Briefing
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                            Play Briefing
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

const LessonPlanViewer: React.FC<{ plan: LessonPlan; onClose: () => void }> = ({ plan, onClose }) => {
    const isJson = typeof plan.content === 'object';
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white w-full max-w-4xl h-[85vh] rounded-lg shadow-2xl flex flex-col overflow-hidden relative">
                <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                    <div>
                        <h3 className="font-bold text-slate-800 text-lg">{plan.title}</h3>
                        <p className="text-xs text-slate-500">Created: {formatLocalDate(plan.createdAt)}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700 p-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto relative bg-slate-50">
                    {isJson ? (
                        <OnlineLessonView moduleId={plan.moduleId} moduleData={{ title: plan.title, content: plan.content }} onBack={onClose} />
                    ) : (
                        <div className="p-8 prose prose-slate max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: md.render(plan.content || '') }} />
                        </div>
                    )}
                </div>
                {!isJson && (
                    <div className="p-4 border-t bg-slate-50 flex justify-end">
                        <button 
                            onClick={() => generateGenericPdf(plan, `Lesson_Plan_${plan.title.replace(/\s+/g, '_')}`)} 
                            className="mr-4 text-blue-600 hover:underline text-sm font-bold"
                        >
                            Download Pro PDF
                        </button>
                        <button onClick={onClose} className="bg-slate-200 text-slate-800 px-4 py-2 rounded hover:bg-slate-300 transition-colors">Close</button>
                    </div>
                )}
            </div>
        </div>
    );
};

const EnrollmentModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onEnroll: (courseOption: CourseOption) => void;
    onBuildPathway: () => void;
    availableCourses: CourseOption[];
}> = ({ isOpen, onClose, onEnroll, onBuildPathway, availableCourses }) => {
    const [selectedId, setSelectedId] = useState<string>('');

    if (!isOpen) return null;

    const handleEnrollClick = () => {
        const course = availableCourses.find(c => c.id === selectedId);
        if (course) {
            onEnroll(course);
            onClose();
        }
    };
    
    const handleBuildClick = () => {
        onBuildPathway();
        onClose();
    };

    const groupedCourses = availableCourses.reduce((acc, course) => {
        if (!acc[course.type]) acc[course.type] = [];
        acc[course.type].push(course);
        return acc;
    }, {} as Record<string, CourseOption[]>);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl flex flex-col max-h-[80vh]">
                <div className="p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-800">Enroll Student in Course</h3>
                    <p className="text-sm text-slate-500">Select a standard curriculum, custom course, or individual module.</p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Career Pathway Option */}
                    <div className="mb-6 p-4 bg-indigo-50 border border-indigo-100 rounded-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="font-bold text-indigo-900 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                    Design Career Pathway
                                </h4>
                                <p className="text-sm text-indigo-700 mt-1">Create a bespoke training program based on this student's prior experience and career goals.</p>
                            </div>
                            <button 
                                onClick={handleBuildClick}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-semibold text-sm whitespace-nowrap shadow-sm"
                            >
                                Build Pathway &rarr;
                            </button>
                        </div>
                    </div>

                    {Object.entries(groupedCourses).map(([type, courses]) => (
                        <div key={type} className="mb-6 last:mb-0">
                            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">{type}</h4>
                            <div className="space-y-2">
                                {(courses as CourseOption[]).map(course => (
                                    <label 
                                        key={course.id} 
                                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${selectedId === course.id ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : 'border-slate-200 hover:border-blue-400'}`}
                                    >
                                        <input 
                                            type="radio" 
                                            name="course_selection" 
                                            value={course.id} 
                                            checked={selectedId === course.id} 
                                            onChange={(e) => setSelectedId(e.target.value)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                        />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-slate-900">{course.title}</span>
                                            <span className="block text-xs text-slate-500">{course.modules.length} Module{course.modules.length !== 1 ? 's' : ''}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 rounded-b-lg">
                    <button onClick={onClose} className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-md font-medium">Cancel</button>
                    <button 
                        onClick={handleEnrollClick} 
                        disabled={!selectedId}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md font-bold hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                        Enroll Student
                    </button>
                </div>
            </div>
        </div>
    );
};

const CertificateView: React.FC<{
    studentName: string;
    courseName: string;
    completionDate: string;
    onClose: () => void;
    studentEmail?: string;
    resultSummary?: AssessmentSummary;
    organizationName?: string;
}> = ({ studentName, courseName, completionDate, onClose, studentEmail, resultSummary, organizationName }) => {
    const certificateId = React.useMemo(() => `CERT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`, []);
    const signatories = getCertificateSignatories(organizationName);

    const handlePrint = () => {
        generateCertificatePdf({
            studentName,
            courseName,
            completionDate,
            certificateId,
            organizationName
        });
    };

    const handleEmail = () => {
        const subject = encodeURIComponent(`Certificate of Completion: ${courseName}`);
        const body = encodeURIComponent(`Dear ${studentName},\n\nCongratulations!\n\nWe are pleased to inform you that you have successfully completed the course "${courseName}" on ${formatLocalDate(completionDate)}.\n\nCertificate ID: ${certificateId}\n\nBest regards,\n\n${signatories[1].name}\nIntelOptics Academy`);
        window.location.href = `mailto:${studentEmail || ''}?subject=${subject}&body=${body}`;
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white w-full max-w-5xl h-[90vh] rounded-lg shadow-2xl flex flex-col overflow-hidden relative">
                <button onClick={onClose} className="absolute top-4 right-4 bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-full z-10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                
                <div className="flex-grow overflow-y-auto">
                     {/* Certificate Area */}
                    <div className="bg-white p-4 md:p-12 flex justify-center items-center relative min-h-[600px]">
                        <LearnerCertificate studentName={studentName} courseName={courseName} completionDate={completionDate}
                            certificateId={certificateId} organizationName={organizationName} />
                    </div>
                    
                    {/* Detailed Results (If Available) */}
                    {resultSummary && (
                        <div className="p-8 bg-slate-50 border-t border-slate-200">
                            <h3 className="text-2xl font-bold text-slate-800 mb-6">Assessment Report</h3>
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-white p-4 rounded border shadow-sm text-center">
                                    <p className="text-slate-500 text-sm uppercase">Final Score</p>
                                    <p className="text-3xl font-bold text-blue-600">{resultSummary.overallScorePercentage}%</p>
                                </div>
                                <div className="bg-white p-4 rounded border shadow-sm text-center">
                                    <p className="text-slate-500 text-sm uppercase">Outcome</p>
                                    <p className={`text-3xl font-bold ${resultSummary.passed ? 'text-green-600' : 'text-red-600'}`}>{resultSummary.passed ? 'Competent' : 'Not Competent'}</p>
                                </div>
                                <div className="bg-white p-4 rounded border shadow-sm text-center">
                                    <p className="text-slate-500 text-sm uppercase">Modules Completed</p>
                                    <p className="text-3xl font-bold text-slate-800">100%</p>
                                </div>
                             </div>
                             
                             {resultSummary.capstoneFeedback && (
                                 <div className="mb-8">
                                     <h4 className="font-bold text-lg text-slate-700 mb-3">Capstone Feedback</h4>
                                     <div className="bg-white p-6 rounded-lg border border-slate-200">
                                         <p className="italic text-slate-600 mb-4">"{resultSummary.capstoneFeedback.overallAssessment}"</p>
                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                             <div>
                                                 <h5 className="font-semibold text-green-700 mb-2">Strengths</h5>
                                                 <div className="prose prose-sm text-slate-600" dangerouslySetInnerHTML={{ __html: md.render(resultSummary.capstoneFeedback.strengths || '')}} />
                                             </div>
                                             <div>
                                                 <h5 className="font-semibold text-amber-700 mb-2">Areas for Improvement</h5>
                                                 <div className="prose prose-sm text-slate-600" dangerouslySetInnerHTML={{ __html: md.render(resultSummary.capstoneFeedback.areasForImprovement || '')}} />
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             )}
                        </div>
                    )}
                </div>

                <div className="bg-slate-50 border-t border-slate-200 p-4 flex justify-end gap-4 flex-shrink-0">
                    <button onClick={handleEmail} className="flex items-center bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-md transition-colors shadow-sm">{React.cloneElement(ICONS.mail as any, {className: 'h-5 w-5 mr-2'})}Send via Email</button>
                    <button onClick={handlePrint} className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition-colors shadow-sm">{React.cloneElement(ICONS.download as any, {className: 'h-5 w-5 mr-2'})}Print / Save as PDF</button>
                </div>
            </div>
        </div>
    );
};

const CareerPathwayBuilder: React.FC<{ 
    onBack: () => void, 
    students: Student[], 
    onAssignPathway: (course: EnrolledCourse, studentId: string) => void,
    initialStudentId?: string 
}> = ({ onBack, students, onAssignPathway, initialStudentId }) => {
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [profile, setProfile] = useState({ currentRole: '', priorEducation: '', experience: '', careerGoal: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<any>(null);

    useEffect(() => {
        if (initialStudentId) {
            const foundStudent = students.find(s => s.id === initialStudentId);
            if (foundStudent) {
                setSelectedStudent(foundStudent);
            }
        }
    }, [initialStudentId, students]);

    const handleSelectStudent = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const student = students.find(s => s.id === e.target.value);
        setSelectedStudent(student || null);
    };

    const handleGenerate = async () => {
        setIsLoading(true);
        try {
            // Flatten modules
            const availableModules = Object.values(MODULE_DATA).map(m => ({ title: m.title }));
            const pathway = await generateCareerPathway(profile, availableModules);
            setResult(pathway);
        } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };

    const handleAssign = () => {
        if (!selectedStudent || !result) return;
        const courseName = result.pathwayTitle || 'Custom Career Pathway';
        const modules = result.recommendedModules?.map((m: any) => m.module) || [];
        
        const newCourse: EnrolledCourse = {
            courseId: `pathway-${Date.now()}`,
            courseName,
            enrollmentDate: localDateInputValue(),
            status: 'Not Started',
            progress: 0,
            modules: modules,
            isCustom: true,
            detailedModules: result.recommendedModules.map((m: any) => ({
                module: m.module,
                level: 'Career Pathway',
                priority: 'High'
            })),
        };
        
        onAssignPathway(newCourse, selectedStudent.id);
        void showAppAlert(`Career Pathway assigned to ${selectedStudent.firstName} ${selectedStudent.lastName}!`);
        onBack();
    };

    return (
        <div className="animate-fade-in max-w-4xl mx-auto">
            <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to Dashboard</button>
            <div className="bg-white p-6 rounded-lg border shadow-sm">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Career Pathway Builder</h2>
                <p className="text-slate-500 mb-6">Design a bespoke training journey based on prior experience (RPL) and future goals.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Select Learner (Optional)</label>
                            <select onChange={handleSelectStudent} value={selectedStudent?.id || ''} className="w-full p-2 border rounded-md bg-slate-50">
                                <option value="">-- Select Learner --</option>
                                {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.organization})</option>)}
                            </select>
                        </div>
                        <div>
                             <label className="block text-sm font-medium text-slate-700 mb-1">Current Role</label>
                             <input value={profile.currentRole} onChange={e => setProfile(p => ({...p, currentRole: e.target.value}))} placeholder="e.g. Retail Security Guard" className="w-full p-2 border rounded-md" />
                        </div>
                        <div>
                             <label className="block text-sm font-medium text-slate-700 mb-1">Prior Education</label>
                             <input value={profile.priorEducation} onChange={e => setProfile(p => ({...p, priorEducation: e.target.value}))} placeholder="e.g. High School Diploma, First Aid Cert" className="w-full p-2 border rounded-md" />
                        </div>
                         <div>
                             <label className="block text-sm font-medium text-slate-700 mb-1">Security/Military Experience</label>
                             <textarea value={profile.experience} onChange={e => setProfile(p => ({...p, experience: e.target.value}))} placeholder="e.g. 4 years Army Infantry, 2 years event security." rows={3} className="w-full p-2 border rounded-md" />
                        </div>
                         <div>
                             <label className="block text-sm font-medium text-slate-700 mb-1">Career Goal</label>
                             <input value={profile.careerGoal} onChange={e => setProfile(p => ({...p, careerGoal: e.target.value}))} placeholder="e.g. Close Protection Officer" className="w-full p-2 border rounded-md" />
                        </div>
                        <button onClick={handleGenerate} disabled={isLoading || !profile.careerGoal} className="w-full bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:bg-slate-300">
                            {isLoading ? 'Analyzing & Building Pathway...' : 'Generate Career Pathway'}
                        </button>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 h-full max-h-[600px] overflow-y-auto">
                        {!result ? (
                            <div className="flex items-center justify-center h-full text-slate-400 text-center">
                                Fill out the profile to generate a custom pathway including RPL credits, training gaps, and OJT.
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800">{result.pathwayTitle}</h3>
                                    <p className="text-sm text-slate-600 mt-2">{result.summary}</p>
                                </div>

                                {result.rplCredits && result.rplCredits.length > 0 && (
                                    <div className="bg-green-50 p-3 rounded border border-green-200">
                                        <h4 className="font-bold text-green-800 text-sm mb-2">Recognition of Prior Learning (RPL)</h4>
                                        <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                                            {result.rplCredits.map((credit: string, i: number) => <li key={i}>{credit}</li>)}
                                        </ul>
                                    </div>
                                )}

                                <div>
                                    <h4 className="font-bold text-slate-700 mb-2">Required Training Modules</h4>
                                    <div className="space-y-2">
                                        {result.recommendedModules.map((mod: any, i: number) => (
                                            <div key={i} className="p-3 bg-white border rounded shadow-sm">
                                                <p className="font-semibold text-sm text-slate-900">{mod.module}</p>
                                                <p className="text-xs text-slate-500 mt-1">{mod.reason}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {result.ojtActivities && result.ojtActivities.length > 0 && (
                                    <div>
                                        <h4 className="font-bold text-slate-700 mb-2">Recommended On-the-Job Training (OJT)</h4>
                                         <ul className="space-y-2">
                                            {result.ojtActivities.map((ojt: any, i: number) => (
                                                <li key={i} className="text-sm text-slate-700 bg-blue-50 p-2 rounded border border-blue-100">
                                                    <strong>{ojt.activity}</strong> <span className="text-slate-500">({ojt.duration})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {selectedStudent && (
                                    <button onClick={handleAssign} className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 font-bold shadow-md">
                                        Assign Pathway to {selectedStudent.firstName}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const TAB_ORDER = ['intro', 'elements', 'competencies', 'range', 'evidence', 'lesson', 'capstone', 'assessment'];
const TAB_LABELS: {[key:string]: string} = {
    intro: 'Introduction',
    elements: 'Elements & Criteria',
    competencies: 'Key Competencies',
    range: 'Range Statement',
    evidence: 'Evidence Guide',
    lesson: 'Interactive Lesson',
    capstone: 'Capstone Scenario',
    assessment: 'Final Assessment'
};

const OnlineLessonView: React.FC<{
    moduleId: string;
    moduleData: any;
    onBack: () => void;
    onComplete?: (summary: AssessmentSummary) => void;
}> = ({ moduleId, moduleData, onBack, onComplete }) => {
    const [activeTab, setActiveTab] = useState('intro');
    const [completedTabs, setCompletedTabs] = useState<Set<string>>(new Set());

    // Quiz State
    const [currentStageIndex, setCurrentStageIndex] = useState(0);
    const [stageAnswers, setStageAnswers] = useState<{[key: string]: string}>({});
    const [showStageFeedback, setShowStageFeedback] = useState<{[key: string]: boolean}>({});
    
    // Capstone State
    const [capstoneResponse, setCapstoneResponse] = useState('');
    const [capstoneFeedback, setCapstoneFeedback] = useState<any>(null);
    const [isGradingCapstone, setIsGradingCapstone] = useState(false);
    
    // Final Assessment State
    const [finalAnswers, setFinalAnswers] = useState<{[key: string]: string}>({});
    const [finalFeedback, setFinalFeedback] = useState<any>(null);
    const [isGradingFinal, setIsGradingFinal] = useState(false);
    const [assessmentComplete, setAssessmentComplete] = useState(false);

    const content = moduleData?.content || {};
    const lesson = moduleData?.lessonContent || content?.lessonContent;

    // Determine avatar contextually
    const isFieldOps = moduleData?.title?.toLowerCase().includes('hazard') || moduleData?.title?.toLowerCase().includes('safety') || moduleData?.title?.toLowerCase().includes('tactical');
    const instructorAvatar = isFieldOps ? "/ai_instructor_field_ops.png" : "/ai_instructor.png";

    const isTabUnlocked = (tabId: string) => {
        const index = TAB_ORDER.indexOf(tabId);
        if (index === 0) return true;
        const prevTab = TAB_ORDER[index - 1];
        return completedTabs.has(prevTab);
    };

    const handleTabClick = (tabId: string) => {
        if (isTabUnlocked(tabId)) {
            setActiveTab(tabId);
        }
    };

    const handleDocAcknowledge = (tab: string) => {
        const newSet = new Set(completedTabs);
        if (newSet.has(tab)) {
            newSet.delete(tab);
        } else {
            newSet.add(tab);
        }
        setCompletedTabs(newSet);
        
        if (!completedTabs.has(tab)) {
            const currentIndex = TAB_ORDER.indexOf(tab);
            if (currentIndex < TAB_ORDER.length - 1) {
                setTimeout(() => setActiveTab(TAB_ORDER[currentIndex + 1]), 500);
            }
        }
    };

    const handleStageAnswer = (scenarioIndex: number, answer: string) => {
        const key = `${currentStageIndex}-${scenarioIndex}`;
        setStageAnswers(prev => ({...prev, [key]: answer}));
        setShowStageFeedback(prev => ({...prev, [key]: true}));
    };

    const handleNextStage = () => {
        if (lesson && lesson.lessonStages && currentStageIndex < lesson.lessonStages.length - 1) {
            setCurrentStageIndex(prev => prev + 1);
        } else {
            const newSet = new Set(completedTabs);
            newSet.add('lesson');
            setCompletedTabs(newSet);
            setActiveTab('capstone');
        }
    };

    const currentScenarios = lesson?.lessonStages?.[currentStageIndex]?.scenarios || [];
    const isStageComplete = currentScenarios.length > 0 && currentScenarios.every((_: any, idx: number) => showStageFeedback[`${currentStageIndex}-${idx}`]);

    const handleCapstoneSubmit = async () => {
        if(!capstoneResponse.trim() || !lesson || !lesson.capstoneScenario) return;
        setIsGradingCapstone(true);
        try {
            const feedback = await evaluateSecurityResponse(lesson.capstoneScenario.scenarioText, capstoneResponse);
            setCapstoneFeedback(feedback);
            
            const newSet = new Set(completedTabs);
            newSet.add('capstone');
            setCompletedTabs(newSet);
        } catch(e) { 
            console.error(e); 
            void showAppAlert("Error grading capstone. Please try again.");
        } finally { 
            setIsGradingCapstone(false); 
        }
    };

    const handleProceedToAssessment = () => {
        setActiveTab('assessment');
    };

    const handleFinalSubmit = async () => {
        if (!lesson || !lesson.finalAssessment) return;
        setIsGradingFinal(true);
        try {
            let totalScore = 0;
            let maxTotal = 0;
            const detailedResults = [];

            for (const task of lesson.finalAssessment.tasks) {
                maxTotal += task.maxScore;
                const userAnswer = finalAnswers[task.id] || '';
                if (task.type === 'multiple-choice') {
                    const isCorrect = userAnswer === task.correctAnswer;
                    const score = isCorrect ? task.maxScore : 0;
                    totalScore += score;
                    detailedResults.push({ taskId: task.id, question: task.question, userAnswer, isCorrect, score, maxScore: task.maxScore });
                } else {
                    const result = await evaluateShortAnswer(userAnswer, task.modelAnswer, task.question, task.maxScore);
                    totalScore += result.score;
                    detailedResults.push({ taskId: task.id, question: task.question, userAnswer, modelAnswer: task.modelAnswer, score: result.score, maxScore: task.maxScore, feedback: result.feedback });
                }
            }
            
            const summary: AssessmentSummary = {
                completionDate: new Date().toISOString(),
                acknowledgedTabs: (Array.from(completedTabs) as string[]).filter(t => ['intro', 'elements', 'competencies', 'range', 'evidence'].includes(t)),
                scenarioScore: { achieved: 0, total: 0 }, 
                finalAssessmentAnswers: detailedResults,
                finalAssessmentScore: { achieved: totalScore, total: maxTotal },
                overallScorePercentage: Math.round((totalScore / maxTotal) * 100),
                passed: (totalScore / maxTotal) >= 0.8,
                capstoneResponse,
                capstoneFeedback
            };
            
            setFinalFeedback(summary);
            setAssessmentComplete(true);
            
            const newSet = new Set(completedTabs);
            newSet.add('assessment');
            setCompletedTabs(newSet);

            if (onComplete) onComplete(summary);

        } catch(e) { 
            console.error(e); 
            void showAppAlert("Error submitting assessment. Please try again.");
        } finally { 
            setIsGradingFinal(false); 
        }
    };
    
    if (!moduleData) {
        return (
            <div className="p-8 flex flex-col items-center justify-center h-full">
                <h2 className="text-xl font-bold text-slate-800 mb-2">Module Not Found</h2>
                <button onClick={onBack} className="text-blue-600 hover:underline">Go Back</button>
            </div>
        );
    }
    
    const getTooltipContent = (itemText: string) => {
        // A simpler mapping for the demo.
        if (itemText.toLowerCase().includes('hazard')) return "Example: Identifying a spill (slip hazard) or frayed wire (electrical hazard). Action: Cordon off area and report to maintenance immediately.";
        if (itemText.toLowerCase().includes('emergency')) return "Example: Fire, Bomb Threat, or Medical Emergency. Action: Follow site-specific ERP (Emergency Response Plan) and evacuate if necessary.";
        if (itemText.toLowerCase().includes('ppe')) return "Example: High-vis vest, steel-cap boots, gloves. Action: Ensure PPE is clean, functional, and worn correctly for the task.";
        if (itemText.toLowerCase().includes('communication')) return "Example: Radio, Phone, Hand Signals. Action: Use clear, concise language and standard pro-words to avoid confusion.";
        return "This is a key range statement item. Security personnel should be familiar with its specific application in their operational environment.";
    };

    return (
        <div className="bg-white h-full flex flex-col">
            <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                <div>
                    <h2 className="text-xl font-bold text-slate-800">{moduleData.title}</h2>
                    <p className="text-sm text-slate-500">Competency Standard & Interactive Lesson</p>
                </div>
                <button onClick={onBack} className="text-sm text-slate-600 hover:text-blue-600">Exit Module</button>
            </div>
            <div className="flex flex-1 overflow-hidden">
                <nav className="w-64 bg-slate-50 border-r overflow-y-auto p-2 space-y-1">
                    {TAB_ORDER.map((tabId, index) => {
                        const isUnlocked = isTabUnlocked(tabId);
                        const isCompleted = completedTabs.has(tabId);
                        const isActive = activeTab === tabId;

                        let icon = <div className="w-4 h-4 rounded-full border border-slate-400 bg-white"></div>;
                        if (isCompleted) icon = <span className="text-green-600 font-bold">✓</span>;
                        else if (isActive) icon = <div className="w-4 h-4 rounded-full border-4 border-blue-500 bg-white"></div>;
                        else if (!isUnlocked) icon = <span className="text-slate-300 text-xs">🔒</span>;

                        return (
                            <button 
                                key={tabId} 
                                onClick={() => handleTabClick(tabId)}
                                disabled={!isUnlocked}
                                className={`w-full text-left px-3 py-3 rounded-md text-sm flex justify-between items-center transition-colors ${
                                    isActive ? 'bg-blue-100 text-blue-800 font-semibold' : 
                                    isUnlocked ? 'hover:bg-slate-100 text-slate-700' : 'text-slate-400 cursor-not-allowed opacity-60'
                                }`}
                            >
                                <span>{TAB_LABELS[tabId]}</span>
                                {icon}
                            </button>
                        );
                    })}
                </nav>

                <main className="flex-1 overflow-y-auto p-8">
                    {['intro', 'elements', 'competencies', 'range', 'evidence'].includes(activeTab) && (
                        <div className="max-w-3xl mx-auto animate-fade-in">
                             <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-md shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-yellow-700 font-medium">
                                            Standard Review Required
                                        </p>
                                        <p className="text-sm text-yellow-600 mt-1">
                                            Please read the following competency standards carefully. You must acknowledge your understanding at the bottom of the page to unlock the next section.
                                        </p>
                                    </div>
                                </div>
                            </div>

                             <div className="prose prose-slate max-w-none mb-8">
                                {activeTab === 'intro' && (
                                    <div className="animate-fade-in">
                                        <AiInstructorCard text={content.introduction || moduleData?.description || ''} title="Module Introduction" avatarUrl={instructorAvatar} />
                                        <p className="text-lg text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(content.introduction || moduleData?.description || '') }} />
                                    </div>
                                )}
                                {activeTab === 'elements' && content.elements && (
                                    <div className="animate-fade-in">
                                        <AiInstructorCard 
                                            title="Elements & Criteria Briefing" 
                                            text={`Let's dive into the Elements and Criteria. First up: ${content.elements[0]?.title}. ${content.elements[0]?.criteria?.join('. ')}`} 
                                            avatarUrl={instructorAvatar} 
                                        />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                            {content.elements.map((el: any, i: number) => (
                                                <div key={i} className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm hover:shadow-md transition-all">
                                                    <div className="flex items-center gap-4 mb-4 border-b border-slate-100 pb-4">
                                                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg">{i + 1}</div>
                                                        <h3 className="font-bold text-lg text-slate-800 leading-tight">{el.title}</h3>
                                                    </div>
                                                    <ul className="space-y-3">
                                                        {el.criteria.map((c: string, j: number) => (
                                                            <li key={j} className="flex items-start text-slate-700 bg-slate-50 p-3 rounded-lg text-sm border border-slate-100">
                                                                <span className="text-blue-500 mr-3 mt-0.5 text-lg leading-none">&bull;</span>
                                                                <span className="leading-snug">{c}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'competencies' && content.keyCompetencies && (
                                    <div className="animate-fade-in">
                                        <AiInstructorCard 
                                            title="Key Competencies Briefing" 
                                            text={content.keyCompetencies.introduction || "Here are the key competencies you will master."} 
                                            avatarUrl={instructorAvatar} 
                                        />
                                        <div className="mt-8">
                                            <p className="text-lg text-slate-700 mb-6" dangerouslySetInnerHTML={{ __html: md.render(content.keyCompetencies.introduction || '') }} />
                                            <div className="grid grid-cols-1 gap-4">
                                                {content.keyCompetencies.competencies.map((c: any, i: number) => (
                                                    <div key={i} className="flex flex-col md:flex-row bg-white rounded-xl border border-indigo-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                                                        <div className="md:w-1/3 bg-indigo-50 p-6 flex flex-col justify-center border-b md:border-b-0 md:border-r border-indigo-100">
                                                            <div className="text-xs font-bold uppercase text-indigo-400 tracking-wider mb-2">Competency {i + 1}</div>
                                                            <h4 className="font-bold text-indigo-900 text-lg">{c.name}</h4>
                                                        </div>
                                                        <div className="md:w-2/3 p-6 flex flex-col justify-center">
                                                            <p className="text-slate-700 mb-3"><strong className="text-slate-900">Application:</strong> {c.application}</p>
                                                            <div className="bg-slate-50 p-3 rounded border border-slate-100 text-sm text-slate-600 italic">
                                                                <span className="font-semibold text-slate-500 not-italic mr-2">Example:</span>
                                                                {c.example}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'range' && content.rangeStatement && (
                                    <div className="animate-fade-in">
                                        <AiInstructorCard 
                                            title="Range Statement Briefing" 
                                            text={content.rangeStatement.introduction || "Let's review the operational environments."} 
                                            avatarUrl={instructorAvatar} 
                                        />
                                        <div className="mt-8">
                                            <p className="text-lg text-slate-700 mb-6" dangerouslySetInnerHTML={{ __html: md.render(content.rangeStatement.introduction || '') }} />
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {content.rangeStatement.variables.map((v: any, i: number) => (
                                                    <div key={i} className="bg-white rounded-xl border border-emerald-100 shadow-sm p-6 hover:shadow-md transition-shadow">
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                            </div>
                                                            <h4 className="font-bold text-lg text-emerald-900">{v.title}</h4>
                                                        </div>
                                                        <ul className="space-y-2">
                                                            {v.items.map((item: string, j: number) => (
                                                                <li key={j} className="flex items-start text-slate-700 p-2 rounded-lg hover:bg-emerald-50 transition-colors cursor-help group relative">
                                                                    <span className="text-emerald-500 mr-2 mt-0.5">•</span>
                                                                    <span>{item}</span>
                                                                    <div className="absolute left-full top-0 ml-2 w-72 p-4 bg-slate-900 text-white text-sm rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 hidden group-hover:block">
                                                                        <div className="font-bold mb-2 text-emerald-300 border-b border-emerald-800 pb-1">Operational Context:</div>
                                                                        {getTooltipContent(item)}
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'evidence' && content.evidenceGuide && (
                                    <div className="animate-fade-in">
                                        <AiInstructorCard 
                                            title="Evidence Guide Briefing" 
                                            text={`The Evidence Guide proves competency. You will be evaluated on: ${content.evidenceGuide.sections.map((s:any)=>s.title).join(", ")}.`} 
                                            avatarUrl={instructorAvatar} 
                                        />
                                        <div className="mt-8 space-y-6">
                                            {content.evidenceGuide.sections.map((s: any, i: number) => (
                                                <div key={i} className="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                                                    <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded bg-amber-200 text-amber-700 font-bold flex items-center justify-center">{i + 1}</div>
                                                        <h3 className="font-bold text-lg text-amber-900">{s.title}</h3>
                                                    </div>
                                                    <div className="p-6">
                                                        <ul className="space-y-4">
                                                            {s.items.map((item: any, j: number) => (
                                                                <li key={j} className="flex items-start bg-slate-50 p-4 rounded-lg border border-slate-100">
                                                                    <div className="mt-1 mr-4 text-amber-500">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                                    </div>
                                                                    <div>
                                                                        {typeof item === 'string' ? (
                                                                            <span className="text-slate-700 font-medium">{item}</span>
                                                                        ) : (
                                                                            <>
                                                                                <strong className="text-slate-800 block text-base mb-1">{item.text}</strong>
                                                                                <div className="text-sm text-slate-500 bg-white p-2 border border-slate-200 rounded mt-2 italic shadow-sm">
                                                                                    <span className="text-amber-600 font-semibold not-italic text-xs uppercase tracking-wider block mb-1">Expected Evidence Base:</span>
                                                                                    {item.example}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="border-t pt-6 mt-6 bg-blue-50 p-4 rounded-lg border-blue-100">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={completedTabs.has(activeTab)} 
                                        onChange={() => handleDocAcknowledge(activeTab)} 
                                        className="w-6 h-6 rounded text-blue-600 focus:ring-blue-500" 
                                    />
                                    <div>
                                        <span className="font-bold text-slate-800">I have read and understood this section.</span>
                                        <p className="text-xs text-slate-500">Check this box to proceed to the next section.</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'lesson' && (
                        <div className="max-w-3xl mx-auto animate-fade-in">
                             <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-md shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-blue-700 font-medium">Interactive Learning</p>
                                        <p className="text-sm text-blue-600 mt-1">
                                            Read each scenario below and select the best course of action. You must select an answer for all scenarios to reveal the feedback and proceed to the next stage.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {(!lesson || !lesson.lessonStages || lesson.lessonStages.length === 0) ? (
                                <div className="p-4 bg-slate-100 rounded border text-center text-slate-500">Lesson content is currently under development.</div>
                            ) : (
                                <>
                                    <AiInstructorCard text={lesson.lessonStages[currentStageIndex]?.content || ''} title={lesson.lessonStages[currentStageIndex]?.title || "Stage Briefing"} avatarUrl={instructorAvatar} />
                                    <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                                        <h3 className="font-bold text-indigo-900">{lesson.lessonStages[currentStageIndex]?.title}</h3>
                                        <p className="mt-2 text-indigo-800" dangerouslySetInnerHTML={{ __html: md.render(lesson.lessonStages[currentStageIndex]?.content || '') }} />
                                    </div>
                                    {lesson.lessonStages[currentStageIndex]?.scenarios?.map((scenario: any, i: number) => {
                                        const answerKey = `${currentStageIndex}-${i}`;
                                        return (
                                            <div key={answerKey} className="mb-8 border rounded-lg overflow-hidden bg-white shadow-sm">
                                                {scenario.mediaUrl && (
                                                    <div className="w-full bg-slate-100 border-b relative">
                                                        {scenario.mediaType === 'video' ? (
                                                            <video src={scenario.mediaUrl} controls autoPlay muted loop className="w-full h-[400px] object-cover" />
                                                        ) : (
                                                            <img src={scenario.mediaUrl} alt="Scenario visual" className="w-full h-[400px] object-cover" />
                                                        )}
                                                        {scenario.mediaCaption && (
                                                            <div className="absolute bottom-0 left-0 w-full bg-black/60 text-white text-xs p-2 backdrop-blur-sm">
                                                                {scenario.mediaCaption}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="p-6">
                                                    <p className="font-semibold mb-4 text-slate-900 border-l-4 border-slate-300 pl-4">{scenario.scenario}</p>
                                                    <p className="mb-4 font-bold text-slate-800">{scenario.question}</p>
                                                    <div className="space-y-2">
                                                        {scenario.options.map((opt: string, j: number) => (
                                                            <button key={j} onClick={() => handleStageAnswer(i, opt)} disabled={!!showStageFeedback[answerKey]}
                                                                className={`w-full text-left p-3 rounded border ${showStageFeedback[answerKey] ? (opt === scenario.correctAnswer ? 'bg-green-100 border-green-500' : (stageAnswers[answerKey] === opt ? 'bg-red-100 border-red-500' : 'bg-slate-50')) : 'hover:bg-slate-50 transition-colors'}`}
                                                            >
                                                                {opt}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {showStageFeedback[answerKey] && (
                                                        <div className={`mt-4 p-4 rounded ${stageAnswers[answerKey] === scenario.correctAnswer ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                                            <strong>{stageAnswers[answerKey] === scenario.correctAnswer ? 'Correct!' : 'Incorrect.'}</strong> {scenario.feedback}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="flex justify-between pt-4 border-t">
                                        <button disabled={currentStageIndex === 0} onClick={() => setCurrentStageIndex(p => p - 1)} className="text-slate-500 disabled:opacity-50">&larr; Previous Stage</button>
                                        <button 
                                            onClick={handleNextStage} 
                                            disabled={!isStageComplete} 
                                            className={`px-6 py-2 rounded-md font-semibold text-white transition-colors ${!isStageComplete ? 'bg-slate-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                        >
                                            {currentStageIndex < lesson.lessonStages.length - 1 ? 'Next Stage' : 'Finish Lesson & Unlock Capstone'} &rarr;
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'capstone' && (
                        <div className="max-w-3xl mx-auto animate-fade-in">
                             <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6 rounded-r-md shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-indigo-700 font-medium">Assessment Task</p>
                                        <p className="text-sm text-indigo-600 mt-1">
                                            Analyze the scenario below and provide a detailed written response. The Insights Assessor will evaluate your submission.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {(!lesson || !lesson.capstoneScenario) ? (
                                <div className="p-4 bg-slate-100 rounded border text-center text-slate-500">Capstone content unavailable.</div>
                            ) : (
                                <>
                                     <AiInstructorCard text={lesson.capstoneScenario.scenarioText || ''} title="Capstone Briefing" avatarUrl={instructorAvatar} />
                                     <div className="bg-white p-6 rounded-lg border border-indigo-200 mb-6 shadow-sm">
                                        <h3 className="text-xl font-bold text-indigo-900 mb-2">{lesson.capstoneScenario.title}</h3>
                                        <p className="text-indigo-800 mb-4 whitespace-pre-wrap">{lesson.capstoneScenario.scenarioText}</p>
                                        <p className="font-semibold text-indigo-900">Instructions: {lesson.capstoneScenario.instructions}</p>
                                    </div>
                                    <textarea 
                                        value={capstoneResponse} 
                                        onChange={e => setCapstoneResponse(e.target.value)} 
                                        rows={8} 
                                        className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-indigo-500" 
                                        placeholder="Type your detailed response here..." 
                                        disabled={!!capstoneFeedback} 
                                    />
                                    {!capstoneFeedback ? (
                                        <button onClick={handleCapstoneSubmit} disabled={isGradingCapstone || !capstoneResponse} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-md font-semibold disabled:bg-slate-400 w-full">
                                            {isGradingCapstone ? 'Insights Assessor is grading...' : 'Submit Response for Assessment'}
                                        </button>
                                    ) : (
                                        <div className="mt-6 animate-fade-in">
                                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                                                <h4 className="font-bold text-green-800 flex items-center gap-2">
                                                    <span className="text-xl">✓</span> Capstone Completed
                                                </h4>
                                                <p className="text-sm text-green-700">Review your feedback below. The final assessment is now unlocked.</p>
                                            </div>
                                            <AnalysisResultDisplay 
                                                result={capstoneFeedback} 
                                                onSave={() => Promise.resolve()} // ADD THIS
                                                isSaving={false}                 // ADD THIS
                                            />
                                            <button onClick={handleProceedToAssessment} className="mt-6 w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700">
                                                Proceed to Final Assessment &rarr;
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'assessment' && (
                        <div className="max-w-3xl mx-auto animate-fade-in">
                             <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6 rounded-r-md shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-2.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-purple-700 font-medium">Final Verification</p>
                                        <p className="text-sm text-purple-600 mt-1">
                                            This is the final knowledge check. It includes both multiple-choice and short-answer questions. You need a score of 80% or higher to pass.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {(!lesson || !lesson.finalAssessment) ? (
                                <div className="p-4 bg-slate-100 rounded border text-center text-slate-500">Assessment content unavailable.</div>
                            ) : !assessmentComplete ? (
                                <>
                                    <div className="mb-6 bg-slate-50 p-6 rounded-lg border border-slate-200">
                                        <h3 className="text-xl font-bold text-slate-800">{lesson.finalAssessment.title}</h3>
                                        <p className="text-slate-600 mt-2">{lesson.finalAssessment.scenario}</p>
                                    </div>
                                    <div className="space-y-8">
                                        {lesson.finalAssessment.tasks.map((task: any) => (
                                            <div key={task.id} className="p-6 border rounded-lg bg-white shadow-sm">
                                                <p className="font-semibold mb-3">{task.question} <span className="text-xs text-slate-400 font-normal ml-2">({task.maxScore} pts)</span></p>
                                                {task.type === 'multiple-choice' ? (
                                                    <div className="space-y-2">{task.options.map((opt: string, i: number) => (
                                                        <label key={i} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded border border-transparent hover:border-slate-200">
                                                            <input type="radio" name={task.id} value={opt} onChange={e => setFinalAnswers(p => ({...p, [task.id]: e.target.value}))} checked={finalAnswers[task.id] === opt} className="w-4 h-4 text-blue-600"/>
                                                            {opt}
                                                        </label>
                                                    ))}</div>
                                                ) : (
                                                    <textarea 
                                                        value={finalAnswers[task.id] || ''} 
                                                        onChange={e => setFinalAnswers(p => ({...p, [task.id]: e.target.value}))} 
                                                        className="w-full p-3 border rounded focus:ring-2 focus:ring-blue-500" 
                                                        rows={3} 
                                                        placeholder="Type your answer..." 
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="sticky bottom-0 bg-white/90 backdrop-blur py-4 mt-8 border-t">
                                        <button 
                                            onClick={handleFinalSubmit} 
                                            disabled={isGradingFinal || Object.keys(finalAnswers).length < lesson.finalAssessment.tasks.length} 
                                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold disabled:bg-slate-400 hover:bg-blue-700 transition-colors"
                                        >
                                            {isGradingFinal ? 'Grading & Finalizing...' : 'Submit Final Assessment'}
                                        </button>
                                        {Object.keys(finalAnswers).length < lesson.finalAssessment.tasks.length && (
                                            <p className="text-xs text-center text-red-500 mt-2">Please answer all questions to submit.</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-12">
                                    <div className={`inline-flex p-4 rounded-full mb-6 ${finalFeedback.passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                        {finalFeedback.passed ? <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
                                    </div>
                                    <h2 className="text-4xl font-bold text-slate-800 mb-2">{finalFeedback.passed ? 'Competent' : 'Not Yet Competent'}</h2>
                                    <p className="text-lg text-slate-600 mb-8">You scored <span className="font-bold">{finalFeedback.finalAssessmentScore.achieved}</span> out of {finalFeedback.finalAssessmentScore.total} ({finalFeedback.overallScorePercentage}%)</p>
                                    
                                    <div className="text-left bg-slate-50 p-6 rounded-lg border mb-8 max-h-96 overflow-y-auto">
                                        <h4 className="font-bold mb-4 text-lg border-b pb-2">Detailed Feedback</h4>
                                        <div className="space-y-6">
                                            {finalFeedback.finalAssessmentAnswers.map((ans: any, i: number) => (
                                                <div key={i} className="pb-4 border-b last:border-0">
                                                    <p className="font-semibold text-slate-800 mb-1">{ans.question}</p>
                                                    <p className="text-sm text-slate-600 italic mb-2">Your Answer: {ans.userAnswer}</p>
                                                    <div className={`text-sm font-bold ${ans.score > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        Score: {ans.score}/{ans.maxScore}
                                                    </div>
                                                    {ans.feedback && <p className="text-sm mt-1 text-slate-700 bg-white p-2 rounded border border-slate-200">Feedback: {ans.feedback}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <button onClick={onBack} className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-900 font-semibold shadow-lg">Return to Dashboard</button>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

const CourseDetail: React.FC<{ courseId: string; onBack: () => void; onStartModule: (modId: string) => void; lessonPlans: {[moduleId: string]: LessonPlan[] } }> = ({ courseId, onBack, onStartModule, lessonPlans }) => {
    let courseTitle = '';
    let modules: { id: string; title: string }[] = [];
    const [viewingPlan, setViewingPlan] = useState<LessonPlan | null>(null);

    const levelKey = Object.keys(LMS_LEVELS).find(key => key === courseId || (key === 'level_1' && courseId === 'L1-SO') || (key === 'level_2' && courseId === 'L2-SO'));
    
    if (levelKey && LMS_LEVELS[levelKey]) {
        courseTitle = LMS_LEVELS[levelKey].title;
        modules = LMS_LEVELS[levelKey].modules;
    } else {
         courseTitle = "Custom Course"; 
         modules = [];
    }

    return (
        <div className="animate-fade-in">
            {viewingPlan && <LessonPlanViewer plan={viewingPlan} onClose={() => setViewingPlan(null)} />}
            
            <button onClick={onBack} className="mb-4 flex items-center text-sm text-slate-500 hover:text-blue-600"><span className="mr-1">&larr;</span> Back to Curriculum</button>
            <h2 className="text-2xl font-bold text-slate-800 mb-6">{courseTitle}</h2>
            <div className="grid gap-4">
                {modules.map(mod => {
                    const hasContent = !!MODULE_DATA[mod.id] && MODULE_DATA[mod.id].content.introduction !== 'Content coming soon.';
                    const savedPlans = lessonPlans[mod.id] || [];
                    
                    return (
                        <div key={mod.id} className="bg-white p-4 rounded-lg border border-slate-200 flex flex-col">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="font-semibold text-slate-900">{mod.title}</h4>
                                    <p className="text-xs text-slate-500">{mod.id}</p>
                                </div>
                                <button 
                                    onClick={() => hasContent ? onStartModule(mod.id) : void showAppAlert('This module is currently under development.')}
                                    className={`px-4 py-2 rounded text-sm font-medium ${hasContent ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                                >
                                    {hasContent ? 'Open Module' : 'Coming Soon'}
                                </button>
                            </div>
                            {savedPlans.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-slate-100">
                                    <p className="text-xs font-semibold text-slate-500 mb-2">SAVED LESSON PLANS</p>
                                    <div className="flex flex-wrap gap-2">
                                        {savedPlans.map(plan => (
                                            <button 
                                                key={plan.id} 
                                                onClick={() => setViewingPlan(plan)}
                                                className="flex items-center text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1 rounded border border-indigo-200 transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                {plan.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CurriculumHub: React.FC<{ onSelectCourse: (courseId: string) => void, onBack: () => void }> = ({ onSelectCourse, onBack }) => {
    return (
        <div className="animate-fade-in">
            <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to Dashboard</button>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Curriculum Hub</h2>
            <p className="text-slate-500 mb-6">Browse standard competency levels and modules.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(LMS_LEVELS).map(([key, level]) => (
                    <div key={key} onClick={() => onSelectCourse(key)} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition-all">
                        <div className="flex items-center mb-4">
                            <div className="p-3 bg-indigo-100 text-indigo-600 rounded-lg mr-4">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">{level.title}</h3>
                        </div>
                        <p className="text-sm text-slate-500 mb-4">{level.modules.length} Competency Modules</p>
                        <ul className="text-sm text-slate-600 space-y-1 mb-4">
                            {level.modules.slice(0, 3).map(m => <li key={m.id}>• {m.title}</li>)}
                            {level.modules.length > 3 && <li className="text-slate-400 italic">+ {level.modules.length - 3} more...</li>}
                        </ul>
                        <div className="text-blue-600 text-sm font-semibold">View Curriculum &rarr;</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StudentList: React.FC<{ 
    students: Student[], 
    onSelectStudent: (s: Student) => void, 
    onBack: () => void,
    organization?: Organization | null,
}> = ({ students, onSelectStudent, onBack, organization }) => {
    const [search, setSearch] = useState('');

    const filtered = students.filter(s => s.lastName.toLowerCase().includes(search.toLowerCase()) || s.organization.toLowerCase().includes(search.toLowerCase()));
    
    return (
        <div className="animate-fade-in relative">
             <div className="flex justify-between items-center mb-6">
                <button onClick={onBack} className="text-sm text-slate-500 hover:text-blue-600">&larr; Back</button>
                <input type="text" placeholder="Search learners..." value={search} onChange={e => setSearch(e.target.value)} className="p-2 border rounded-md" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Learner Administration</h2>
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50"><tr><th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th><th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Org</th><th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th><th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th></tr></thead>
                    <tbody className="divide-y divide-slate-200">
                        {filtered.map(s => (
                            <tr key={s.id} className="hover:bg-slate-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{s.lastName}, {s.firstName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{s.organization}</td>
                                <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{subscriptionStatusLabel(s.subscriptionStatus, organization?.id === s.organizationId && organization?.isTestOrganization === true)}</span></td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium"><button onClick={() => onSelectStudent(s)} className="text-blue-600 hover:text-blue-900">View Details</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const StudentDetail: React.FC<{ 
    student: Student, 
    onBack: () => void, 
    onEnroll: (courseId: string, courseName: string, modules: string[], isCustom?: boolean) => void,
    onBuildPathway: () => void,
    standardCourses: any[],
    customCourses: any[]
}> = ({ student, onBack, onEnroll, onBuildPathway, standardCourses, customCourses }) => {
    const [showCertificate, setShowCertificate] = useState<EnrolledCourse | null>(null);
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

    const allCourses: CourseOption[] = [
        ...standardCourses.map(c => ({ id: c.id, title: c.courseName, type: 'Standard' as const, modules: c.modules })),
        ...customCourses.map(c => ({ id: c.id, title: c.courseName, type: 'Custom Course' as const, modules: c.modules }))
    ];

    return (
        <div className="animate-fade-in">
            {showCertificate && <CertificateView studentName={`${student.firstName} ${student.lastName}`} courseName={showCertificate.courseName} completionDate={showCertificate.assessmentSummary?.completionDate || new Date().toISOString()} onClose={() => setShowCertificate(null)} studentEmail={student.email} resultSummary={showCertificate.assessmentSummary} />}
            
            <EnrollmentModal 
                isOpen={isEnrollModalOpen}
                onClose={() => setIsEnrollModalOpen(false)}
                availableCourses={allCourses}
                onBuildPathway={onBuildPathway}
                onEnroll={(course) => {
                    onEnroll(course.id, course.title, course.modules, course.type !== 'Standard');
                }}
            />
            
            <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to List</button>
            <div className="bg-white p-6 rounded-xl border shadow-sm mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">{student.firstName} {student.lastName}</h2>
                        <p className="text-slate-500">{student.organization} | ID: {student.studentNumber}</p>
                        <div className="mt-2 text-xs text-slate-400">Account: <span className="text-slate-600">{student.email}</span></div>
                    </div>
                    <button onClick={() => setIsEnrollModalOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-semibold shadow-sm">Enroll in New Course</button>
                </div>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-4">Enrolled Courses</h3>
            <div className="space-y-4">
                {student.enrolledCourses.length === 0 ? (
                    <div className="text-slate-500 italic p-4 border border-dashed rounded-lg text-center">No courses enrolled.</div>
                ) : (
                    student.enrolledCourses.map(course => (
                        <div key={course.courseId} className="bg-white p-6 rounded-lg border shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h4 className="font-bold text-slate-900 text-lg">{course.courseName}</h4>
                                    <p className="text-sm text-slate-500">Status: <span className={`font-semibold ${course.status === 'Completed' ? 'text-green-600' : 'text-blue-600'}`}>{course.status}</span></p>
                                </div>
                                {course.status === 'Completed' && <button onClick={() => setShowCertificate(course)} className="text-blue-600 text-sm hover:underline">View Results & Certificate</button>}
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2.5 mb-4">
                                <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${course.progress}%` }}></div>
                            </div>
                            {course.competencyResults && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                                    {Object.entries(course.competencyResults).map(([comp, score]) => (
                                        <div key={comp} className="bg-slate-50 p-3 rounded border text-center">
                                            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{comp}</div>
                                            <div className="text-xl font-bold text-slate-800">{score}%</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {course.assessmentSummary?.capstoneFeedback && (
                                <div className="mt-4 pt-4 border-t">
                                    <h5 className="font-semibold text-sm mb-2">Latest Instructor Feedback (Capstone)</h5>
                                    <p className="text-sm text-slate-600 italic">"{course.assessmentSummary.capstoneFeedback.overallAssessment}"</p>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const LmsContentBuilder: React.FC<{ onBack: () => void, onCreateCustomModule: (m: any) => void, onSaveLessonPlan: (moduleId: string, title: string, content: string) => void }> = ({ onBack, onCreateCustomModule, onSaveLessonPlan }) => {
    const [activeTab, setActiveTab] = useState<'module' | 'lesson'>('module');
    
    // State for Module Builder
    const [title, setTitle] = useState('');
    const [objective, setObjective] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<any>(null);

    // State for Lesson Plan Architect
    const [lpModuleId, setLpModuleId] = useState('');
    const [lpTopic, setLpTopic] = useState('');
    const [lpAudience, setLpAudience] = useState('New Recruits');
    const [lpSelectedCriteria, setLpSelectedCriteria] = useState<Set<string>>(new Set());
    const [lpSelectedRange, setLpSelectedRange] = useState<Set<string>>(new Set());
    const [lpInstructions, setLpInstructions] = useState('');
    const [lpResult, setLpResult] = useState<any>(null);
    const [isLpLoading, setIsLpLoading] = useState(false);
    const [planTitle, setPlanTitle] = useState('');

    // Handlers
    const handleModuleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const modId = e.target.value;
        setLpModuleId(modId);
        if (modId && MODULE_DATA[modId]) {
            setLpTopic(MODULE_DATA[modId].title);
        } else {
            setLpTopic('');
        }
        // Reset selections
        setLpSelectedCriteria(new Set());
        setLpSelectedRange(new Set());
        setLpResult(null);
        setPlanTitle('');
    };

    const toggleCriteria = (criteria: string) => {
        const newSet = new Set(lpSelectedCriteria);
        if (newSet.has(criteria)) newSet.delete(criteria);
        else newSet.add(criteria);
        setLpSelectedCriteria(newSet);
    };

    const toggleRange = (item: string) => {
        const newSet = new Set(lpSelectedRange);
        if (newSet.has(item)) newSet.delete(item);
        else newSet.add(item);
        setLpSelectedRange(newSet);
    };

    const handleGenerateModule = async () => {
        setIsLoading(true);
        try {
            const moduleData = await generateCustomLmsModule(title, objective);
            setResult(moduleData);
            const newModuleId = `CUSTOM-MOD-${Date.now()}`;
            onCreateCustomModule({ ...moduleData, title, id: newModuleId }); 
        } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };

    const handleGenerateLessonPlan = async () => {
        setIsLpLoading(true);
        try {
            // Format lists for the prompt
            const criteriaList = Array.from(lpSelectedCriteria).map(c => `- ${c}`).join('\n');
            const rangeList = Array.from(lpSelectedRange).map(r => `- ${r}`).join('\n');

            const planJson = await generateLessonPlan({
                topic: lpTopic,
                audience: lpAudience,
                criteria: criteriaList || "No specific criteria selected.",
                rangeStatement: rangeList || "No specific range variables selected.",
                trainerInstructions: lpInstructions
            });
            setLpResult(planJson);
            setPlanTitle(`Lesson Plan - ${formatLocalDate(new Date())}`);
        } catch (e) { console.error(e); } finally { setIsLpLoading(false); }
    };
    
    const handleSavePlan = () => {
        if (lpModuleId && planTitle && lpResult) {
            onSaveLessonPlan(lpModuleId, planTitle, lpResult);
            void showAppAlert('Lesson plan saved to Curriculum Hub.');
            // Reset
            setLpResult(null);
            setPlanTitle('');
            setLpInstructions('');
        }
    };

    const currentModuleData = lpModuleId ? MODULE_DATA[lpModuleId] : null;

    return (
        <div className="animate-fade-in">
            <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to Dashboard</button>
            
            <div className="flex mb-4 border-b border-slate-200">
                <button
                    onClick={() => setActiveTab('module')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'module' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Module Generator
                </button>
                <button
                    onClick={() => setActiveTab('lesson')}
                    className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'lesson' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                >
                    Lesson Plan Architect
                </button>
            </div>

            {activeTab === 'module' ? (
                <div className="bg-white p-6 rounded-lg border shadow-sm max-w-3xl mx-auto">
                    <h2 className="text-2xl font-bold text-slate-800 mb-4">Insights Content Builder</h2>
                    <p className="text-slate-500 mb-6">Generate a complete competency module and interactive lesson from a simple description.</p>
                    <div className="space-y-4">
                        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Module Title (e.g. Advanced Crowd Control)" className="w-full p-2 border rounded" />
                        <textarea value={objective} onChange={e => setObjective(e.target.value)} placeholder="Learning Objective (e.g. Learners must be able to identify crowd density risks and apply formation tactics...)" rows={4} className="w-full p-2 border rounded" />
                        <button onClick={handleGenerateModule} disabled={isLoading || !title || !objective} className="w-full bg-purple-600 text-white py-2 rounded hover:bg-purple-700 disabled:bg-slate-300">
                            {isLoading ? 'Generating Content...' : 'Generate Module'}
                        </button>
                    </div>
                    {result && (
                        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded text-green-800 relative z-0">
                            <h3 className="font-bold flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Module Generated Successfully!</h3>
                            <p className="text-sm mt-1 mb-4">The module has been added to the custom curriculum library.</p>
                            <div className="mt-4 bg-white rounded-lg border border-green-200 shadow-sm h-[500px] overflow-hidden relative">
                                <OnlineLessonView 
                                    moduleId={result.id || 'preview'} 
                                    moduleData={result} 
                                    onBack={() => setResult(null)} 
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white p-6 rounded-lg border shadow-sm max-w-5xl mx-auto">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Lesson Plan Architect</h2>
                    <p className="text-slate-500 mb-6">Design a structured 40-minute training session by selecting specific competency elements.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Select Module</label>
                                <select value={lpModuleId} onChange={handleModuleChange} className="w-full p-2 border rounded-md bg-slate-50">
                                    <option value="">-- Select a Competency Module --</option>
                                    {Object.entries(LMS_LEVELS).map(([key, level]) => (
                                        <optgroup key={key} label={level.title}>
                                            {level.modules.map(m => (
                                                <option key={m.id} value={m.id}>{m.title}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                            <div>
                                 <label className="block text-sm font-medium text-slate-700 mb-1">Lesson Topic</label>
                                 <input value={lpTopic} onChange={e => setLpTopic(e.target.value)} placeholder="e.g., Conducting a Vehicle Search" className="w-full p-2 border rounded-md" />
                            </div>
                            <div>
                                 <label className="block text-sm font-medium text-slate-700 mb-1">Target Audience</label>
                                 <input value={lpAudience} onChange={e => setLpAudience(e.target.value)} placeholder="e.g., New Recruits" className="w-full p-2 border rounded-md" />
                            </div>
                             <div>
                                 <label className="block text-sm font-medium text-slate-700 mb-1">Trainer's Context & Objectives</label>
                                 <textarea value={lpInstructions} onChange={e => setLpInstructions(e.target.value)} placeholder="e.g., Focus on practical demonstrations. The class has limited experience with radios." rows={4} className="w-full p-2 border rounded-md text-sm" />
                            </div>
                        </div>

                        <div className="space-y-6 h-[500px] overflow-y-auto pr-2">
                            {currentModuleData ? (
                                <>
                                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                        <h3 className="font-bold text-slate-800 mb-3 flex justify-between items-center">
                                            Performance Criteria
                                            <span className="text-xs font-normal text-slate-500 bg-white px-2 py-1 rounded border">{lpSelectedCriteria.size} selected</span>
                                        </h3>
                                        <div className="space-y-4">
                                            {currentModuleData.content.elements?.map((el: any, idx: number) => (
                                                <div key={idx}>
                                                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase">{el.title}</p>
                                                    <div className="space-y-1">
                                                        {el.criteria.map((crit: string, cIdx: number) => (
                                                            <label key={cIdx} className="flex items-start gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={lpSelectedCriteria.has(crit)}
                                                                    onChange={() => toggleCriteria(crit)}
                                                                    className="mt-1" 
                                                                />
                                                                <span className="text-sm text-slate-700">{crit}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                        <h3 className="font-bold text-slate-800 mb-3 flex justify-between items-center">
                                            Range Statement
                                            <span className="text-xs font-normal text-slate-500 bg-white px-2 py-1 rounded border">{lpSelectedRange.size} selected</span>
                                        </h3>
                                        <div className="space-y-4">
                                            {currentModuleData.content.rangeStatement?.variables?.map((variable: any, idx: number) => (
                                                <div key={idx}>
                                                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase">{variable.title}</p>
                                                    <div className="space-y-1">
                                                        {variable.items.map((item: string, iIdx: number) => (
                                                            <label key={iIdx} className="flex items-start gap-2 p-2 hover:bg-white rounded cursor-pointer transition-colors">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={lpSelectedRange.has(item)}
                                                                    onChange={() => toggleRange(item)}
                                                                    className="mt-1" 
                                                                />
                                                                <span className="text-sm text-slate-700">{item}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="h-full flex items-center justify-center bg-slate-50 rounded-lg border border-dashed text-slate-400 text-sm p-8 text-center">
                                    Select a module from the dropdown to view available criteria and range variables.
                                </div>
                            )}
                        </div>
                    </div>

                    <button onClick={handleGenerateLessonPlan} disabled={isLpLoading || !lpModuleId || lpSelectedCriteria.size === 0} className="w-full bg-indigo-600 text-white py-3 rounded-md hover:bg-indigo-700 disabled:bg-slate-300 font-semibold mb-6 transition-colors">
                        {isLpLoading ? 'Architecting Lesson Plan...' : 'Generate Lesson Plan'}
                    </button>

                    {lpResult && (
                        <div className="mt-8 border-t pt-6 animate-fade-in relative z-0">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-slate-800">Generated Lesson Plan</h3>
                                <div className="flex gap-2">
                                     <input 
                                        type="text" 
                                        value={planTitle} 
                                        onChange={e => setPlanTitle(e.target.value)} 
                                        placeholder="Lesson Plan Title" 
                                        className="p-2 border rounded-md text-sm"
                                    />
                                    <button onClick={handleSavePlan} disabled={!planTitle} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:bg-slate-300">
                                        Save to Curriculum
                                    </button>
                                </div>
                            </div>
                            <div className="bg-slate-50 border rounded-lg shadow-sm h-[600px] overflow-hidden relative">
                                <OnlineLessonView 
                                    moduleId={lpModuleId || 'preview'} 
                                    moduleData={{ title: planTitle || 'Lesson Plan Preview', content: typeof lpResult === 'object' ? lpResult.content : lpResult }} 
                                    onBack={() => setLpResult(null)} 
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const AiCompetencyAdvisor: React.FC<{ onBack: () => void }> = ({ onBack }) => {
    const [objective, setObjective] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<any>(null);

    const handleAnalyze = async () => {
        setIsLoading(true);
        try {
            const res = await generateCompetencyPathwayFromObjective(objective);
            setResult(res);
        } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };

    return (
        <div className="animate-fade-in">
             <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to Dashboard</button>
            <div className="bg-white p-6 rounded-lg border shadow-sm max-w-3xl mx-auto">
                 <h2 className="text-2xl font-bold text-slate-800 mb-4">Insights Training & Development Advisor</h2>
                 <p className="text-slate-500 mb-6">Describe a training goal, and Insights will recommend a learning pathway from the existing curriculum.</p>
                 <textarea value={objective} onChange={e => setObjective(e.target.value)} placeholder="e.g., I need a pathway for a new supervisor who needs to understand legal liabilities and team leadership." rows={4} className="w-full p-2 border rounded mb-4" />
                 <button onClick={handleAnalyze} disabled={isLoading || !objective} className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:bg-slate-300">
                    {isLoading ? 'Analyzing Curriculum...' : 'Get Recommendations'}
                </button>
                
                {result && (
                    <div className="mt-6 space-y-6">
                        <div className="bg-slate-50 p-4 rounded border prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: md.render(result.analysisRationale || '') }} />
                        <div>
                            <h4 className="font-bold text-slate-800 mb-2">Recommended Modules</h4>
                            <ul className="space-y-2">
                                {result.recommendedModules.map((m: any, i: number) => (
                                    <li key={i} className="flex justify-between items-center p-3 bg-white border rounded shadow-sm">
                                        <div>
                                            <span className="font-semibold text-slate-900">{m.module}</span>
                                            <span className="block text-xs text-slate-500">{m.level}</span>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${m.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{m.priority}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const LmsReporting: React.FC<{ onBack: () => void; students: Student[] }> = ({ onBack, students }) => {
    const data = useMemo(() => {
        const courses = new Map<string, { name: string; completed: number; inProgress: number }>();
        for (const student of students) {
            for (const course of student.enrolledCourses || []) {
                const entry = courses.get(course.courseName) || { name: course.courseName, completed: 0, inProgress: 0 };
                if (course.status === 'Completed')
                    entry.completed++;
                else
                    entry.inProgress++;
                courses.set(course.courseName, entry);
            }
        }
        return Array.from(courses.values());
    }, [students]);
    const recentCertifications = useMemo(() => students.flatMap(student =>
        (student.enrolledCourses || [])
            .filter(course => course.status === 'Completed')
            .map(course => ({
                learner: `${student.firstName} ${student.lastName}`.trim(),
                course: course.courseName,
                date: course.assessmentSummary?.completionDate || course.enrollmentDate,
            })))
        .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
        .slice(0, 10), [students]);

    return (
        <div className="animate-fade-in">
             <button onClick={onBack} className="mb-6 text-sm text-slate-500 hover:text-blue-600">&larr; Back to Dashboard</button>
             <h2 className="text-2xl font-bold text-slate-800 mb-6">Training Analytics</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border shadow-sm">
                    <h3 className="font-bold text-slate-700 mb-4">Course Status</h3>
                    <div className="h-64 w-full">
                        {data.length ? <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="completed" fill="#10b981" name="Completed" />
                                <Bar dataKey="inProgress" fill="#3b82f6" name="In Progress" />
                            </BarChart>
                        </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-sm text-slate-500">No course activity yet.</div>}
                    </div>
                </div>
                 <div className="bg-white p-6 rounded-xl border shadow-sm">
                    <h3 className="font-bold text-slate-700 mb-4">Recent Certifications</h3>
                    <ul className="space-y-3">
                        {recentCertifications.length ? recentCertifications.map((certification, index) => (
                            <li key={`${certification.learner}-${certification.course}-${index}`} className="flex justify-between items-center gap-4 border-b pb-2">
                                <span>{certification.learner} ({certification.course})</span>
                                <span className="text-xs text-slate-500 whitespace-nowrap">{formatLocalDate(certification.date)}</span>
                            </li>
                        )) : <li className="text-sm text-slate-500">No certifications completed yet.</li>}
                    </ul>
                </div>
             </div>
        </div>
    );
};

const TrainerDashboard: React.FC<{ onNavigate: (view: string) => void; onBackToRole: () => void; profile: TrainerProfile }> = ({ onNavigate, onBackToRole, profile }) => {
    const cards = [
        { title: 'Learner Administration', description: 'Manage learner enrollments and track overall progress.', icon: ICONS.folder, view: 'studentList' },
        { title: 'Curriculum Management', description: 'Explore and manage competency standards and training modules.', icon: ICONS.mail, view: 'curriculum' },
        { title: 'Insights Content Builder', description: 'Generate training modules and structured lesson plans.', icon: ICONS.folder_plus, view: 'builder' },
        { title: 'Competency Advisor', description: 'Generate tailored learning pathways for specific roles.', icon: ICONS.logo, view: 'advisor' },
        { title: 'Career Pathway Builder', description: 'Build career roadmaps with RPL and OJT for learners.', icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>, view: 'careerPathway' },
        { title: 'Analytics & Reporting', description: 'Visualize learner performance and completion rates.', icon: <span className="text-2xl">📊</span>, view: 'reporting' },
    ];
    return (
        <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6">
                 <button onClick={onBackToRole} className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600">
                    &larr; Back
                </button>
                <div className="text-right">
                    <p className="text-sm font-bold text-slate-800">{profile.name}</p>
                    <p className="text-xs text-slate-500">{profile.organization}</p>
                </div>
            </div>
            
            <h3 className="text-2xl font-bold text-slate-800">Trainer & Assessor Dashboard</h3>
            <p className="text-slate-500 mb-8">Administration tools for managing the education program.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cards.map(card => (
                    <div key={card.view} onClick={() => onNavigate(card.view)} className="group cursor-pointer bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                        <div className="p-3 rounded-lg bg-blue-100 text-blue-600 self-start mb-4 inline-block">
                             {React.isValidElement(card.icon) ? React.cloneElement(card.icon as any, {className: 'h-7 w-7'}) : card.icon}
                        </div>
                        <h4 className="font-bold text-slate-900">{card.title}</h4>
                        <p className="text-sm text-slate-600 mt-2">{card.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LearnerDashboard: React.FC<{ 
    student: Student, 
    onSelectCourse: (course: EnrolledCourse) => void | Promise<void>,
    onLogout: () => void,
    onViewResult: (course: EnrolledCourse) => void
}> = ({ student, onSelectCourse, onLogout, onViewResult }) => {
    const [preparingCourseId, setPreparingCourseId] = useState<string | null>(null);
    const overallProgress = student.enrolledCourses.length
        ? Math.round(student.enrolledCourses.reduce((total, course) => total + Math.max(0, Math.min(100, Number(course.progress) || 0)), 0) / student.enrolledCourses.length)
        : 0;

    return (
        <div className="animate-fade-in bg-[#0B1121] min-h-full p-4 md:p-8 text-white relative overflow-hidden">
            {/* Ambient Background */}
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-indigo-600/20 blur-[120px] rounded-full pointer-events-none" />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 relative z-10 glass-panel p-6">
                <div>
                    <h2 className="text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 tracking-tight">Welcome back, {student.firstName}</h2>
                    <p className="text-slate-400 mt-1 flex items-center">
                        <span className="w-2 h-2 rounded-full bg-blue-500 mr-2 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></span>
                        IntelOptics Academy Portal
                    </p>
                </div>
                <div className="mt-4 md:mt-0">
                    <button onClick={onLogout} className="text-xs font-bold uppercase tracking-widest text-red-400 hover:text-white bg-red-500/10 hover:bg-red-500/30 border border-red-500/20 hover:border-red-500/50 px-5 py-2.5 rounded-md transition-all shadow-lg">
                        Back
                    </button>
                </div>
            </div>

            {/* Dashboard Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 relative z-10">
                <div className="glass-panel p-6 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Active Courses</p>
                    <p className="text-4xl font-black text-white">{student.enrolledCourses.filter(c => c.status === 'In Progress' || c.status === 'Not Started').length}</p>
                </div>
                <div className="glass-panel p-6 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Completed Certifications</p>
                    <p className="text-4xl font-black text-emerald-400 shadow-emerald-400/20">{student.enrolledCourses.filter(c => c.status === 'Completed').length}</p>
                </div>
                <div className="glass-panel p-6 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Overall Progress</p>
                    <div className="flex items-center mt-2">
                        <div className="flex-grow bg-slate-800 rounded-full h-2 mr-4">
                            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full" style={{ width: `${overallProgress}%` }}></div>
                        </div>
                        <span className="text-sm font-bold text-blue-400">{overallProgress}%</span>
                    </div>
                </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-6 relative z-10">Your Training Plan</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                {student.enrolledCourses.length === 0 ? (
                    <div className="col-span-full text-center py-16 glass-panel border-dashed border-slate-700/50">
                        <div className="w-16 h-16 mx-auto mb-4 text-slate-600">
                            {ICONS.layers}
                        </div>
                        <p className="text-slate-400 text-lg">You are not enrolled in any courses yet.</p>
                        <p className="text-slate-500 text-sm mt-2">Contact your Training Administrator to get started.</p>
                    </div>
                ) : (
                    student.enrolledCourses.map(course => (
                        <div key={course.courseId} className="glass-panel group hover:border-blue-500/50 transition-all duration-300 flex flex-col h-full overflow-hidden relative">
                            {course.status === 'Completed' && (
                                <div className="absolute -right-10 top-6 rotate-45 bg-emerald-600/80 text-white text-[10px] font-bold uppercase tracking-widest py-1 px-12 shadow-lg backdrop-blur-sm z-10">
                                    Certified
                                </div>
                            )}
                            <div className="p-6 flex-grow flex flex-col">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="pr-4">
                                        <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded border mb-3 ${
                                            course.status === 'Completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                                            course.status === 'In Progress' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 
                                            'bg-slate-700/50 text-slate-400 border-slate-600/50'
                                        }`}>
                                            {course.status}
                                        </span>
                                        <h3 className="text-lg font-bold text-white leading-tight group-hover:text-blue-400 transition-colors">{course.courseName}</h3>
                                        <p className="text-xs text-slate-500 mt-2 font-mono">Enrolled: {formatLocalDate(course.enrollmentDate)}</p>
                                    </div>
                                    <div className={`h-10 w-10 flex-shrink-0 rounded-lg flex items-center justify-center border shadow-inner ${
                                        course.status === 'Completed' ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' :
                                        'bg-slate-800 border-slate-700 text-slate-500 group-hover:bg-blue-900/40 group-hover:border-blue-500/40 group-hover:text-blue-400 transition-colors'
                                    }`}>
                                        {course.status === 'Completed' ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="mt-auto pt-4">
                                    <div className="flex justify-between items-end mb-1">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Progress</span>
                                        <span className="text-xs font-bold text-white">{course.progress}%</span>
                                    </div>
                                    <div className="w-full bg-slate-800/80 rounded-full h-1.5 mb-6 overflow-hidden">
                                        <div className={`h-1.5 rounded-full transition-all duration-1000 ${
                                            course.status === 'Completed' ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                                        }`} style={{ width: `${course.progress}%` }}></div>
                                    </div>
                                    
                                    {course.status === 'Completed' ? (
                                         <button 
                                            onClick={() => onViewResult(course)}
                                            className="w-full py-2.5 px-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-widest rounded hover:bg-emerald-500/20 transition-all flex items-center justify-center shadow-lg"
                                        >
                                            View Results & Certificate
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={async () => {
                                                setPreparingCourseId(course.courseId);
                                                try {
                                                    await onSelectCourse(course);
                                                } finally {
                                                    setPreparingCourseId(null);
                                                }
                                            }}
                                            disabled={preparingCourseId !== null}
                                            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 disabled:cursor-wait text-white text-xs font-bold uppercase tracking-widest rounded transition-all flex items-center justify-center shadow-lg shadow-blue-600/30 group-hover:shadow-blue-500/50"
                                        >
                                            {preparingCourseId === course.courseId ? 'Preparing Module...' : course.status === 'Not Started' ? 'Commence Training' : 'Resume Learning'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};


const LearningManagementSuite: React.FC<any> = (props) => {
    const authenticatedRole = props.currentUser?.role;
    const resolveAuthenticatedLearner = (): Student | null => {
        if (!props.currentUser)
            return null;
        const email = String(props.currentUser.email || '').trim().toLowerCase();
        const matched = (props.students || []).find((student: Student) =>
            String(student.email || '').trim().toLowerCase() === email || student.id === props.currentUser.uid);
        if (matched)
            return matched;
        const names = String(props.currentUser.displayName || props.currentUser.email || 'Learner').trim().split(/\s+/);
        return {
            id: props.currentUser.uid,
            learnerId: props.currentUser.uid,
            accessCode: '',
            firstName: names[0] || 'Learner',
            lastName: names.slice(1).join(' '),
            organization: props.currentOrganization?.name || 'Organization',
            organizationId: props.currentOrganization?.id,
            email: props.currentUser.email,
            enrolledCourses: [],
        };
    };
    const [view, setView] = useState(() => authenticatedRole === 'trainer'
        ? 'trainerDashboard'
        : authenticatedRole === 'learner' ? 'learnerDashboard' : 'roleSelection');
    const trainerProfile: TrainerProfile | null = props.currentUser ? {
        id: props.currentUser.uid,
        userId: props.currentUser.uid,
        name: props.currentUser.displayName || props.currentUser.email,
        organization: props.currentOrganization?.name || 'Organization',
        accessCode: '',
    } : null;
    const [currentLearner, setCurrentLearner] = useState<Student | null>(() =>
        authenticatedRole === 'learner' ? resolveAuthenticatedLearner() : null);
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null); // For Curriculum Hub
    const [activeModuleId, setActiveModuleId] = useState<string | null>(null); // For Lesson View
    const [activeModuleData, setActiveModuleData] = useState<any>(null);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null); // For Student Detail
    const [currentEnrolledCourse, setCurrentEnrolledCourse] = useState<EnrolledCourse | null>(null); // To track which specific enrollment is being taken
    
    const [lessonPlans, setLessonPlans] = useState<{[moduleId: string]: LessonPlan[]}>({});
    const [learnerResultToView, setLearnerResultToView] = useState<EnrolledCourse | null>(null);

    useEffect(() => {
        try {
            const storedPlans = clientWorkspaceStorage().getItem('lms_lesson_plans');
            if (storedPlans) setLessonPlans(JSON.parse(storedPlans));
        } catch (e) { console.error("Failed to load LMS data", e); }
    }, []);

    // Sync current learner with updated props.students to ensure latest progress is reflected
    useEffect(() => {
        if (currentLearner && props.students) {
            const email = String(props.currentUser?.email || '').trim().toLowerCase();
            const updatedLearner = props.students.find((s: Student) =>
                s.id === currentLearner.id || (email && String(s.email || '').trim().toLowerCase() === email));
            if (updatedLearner) {
                setCurrentLearner(updatedLearner);
            }
        }
    }, [props.students, currentLearner?.id, props.currentUser?.email]);
    
    const handleSaveLessonPlan = (moduleId: string, title: string, content: any) => {
        const newPlan: LessonPlan = {
            id: `lp-${Date.now()}`,
            title,
            content,
            moduleId,
            createdAt: new Date().toISOString()
        };
        
        setLessonPlans(prev => {
            const updated = { ...prev };
            if (!updated[moduleId]) updated[moduleId] = [];
            updated[moduleId] = [newPlan, ...updated[moduleId]];
            
            try {
                clientWorkspaceStorage().setItem('lms_lesson_plans', JSON.stringify(updated));
            } catch (e) { console.error("Failed to save lesson plans", e); }
            
            return updated;
        });
    };

    const handleEnrollStudent = (courseId: string, courseName: string, modules: string[], isCustom: boolean = false) => {
        if (!selectedStudent || !props.onEnrollStudent) return;
        
        const newCourse: EnrolledCourse = {
            courseId: isCustom ? courseId : `enroll-${selectedStudent.id}-${courseId}-${Date.now()}`,
            courseName: courseName,
            enrollmentDate: localDateInputValue(),
            status: 'Not Started',
            progress: 0,
            modules: modules,
            isCustom: isCustom,
            competencyResults: {},
        };
        
        props.onEnrollStudent(newCourse, selectedStudent.id);
    };
    
    const standardCourseDefinitions = useMemo(() => {
        if (props.standardCourseDefinitions?.length)
            return props.standardCourseDefinitions;
        return Object.entries(LMS_LEVELS).map(([id, level]) => ({
            id,
            courseName: level.title,
            modules: level.modules.map(module => module.id),
        }));
    }, [props.standardCourseDefinitions]);

    const openRolePortal = (role: 'trainer' | 'learner') => {
        if (role === 'learner')
            setCurrentLearner(resolveAuthenticatedLearner());
        setView(role === 'trainer' ? 'trainerDashboard' : 'learnerDashboard');
    };
    const exitRolePortal = () => {
        if (authenticatedRole === 'owner' || authenticatedRole === 'admin')
            setView('roleSelection');
        else
            props.onBack?.();
    };

    
    // --- View Routing ---
    const isMaintenanceMode = (props as any).maintenanceMode !== false; // Active by default
    if (isMaintenanceMode) {
        return (
            <div className="flex bg-slate-50 min-h-[calc(100vh-4rem)] p-8 w-full items-center justify-center">
                <div className="max-w-xl w-full text-center py-16 bg-white rounded-xl shadow-sm border border-slate-200 px-8">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">Learning Management Suite</h2>
                    <div className="inline-block bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-1 rounded-full font-medium mb-6">
                        System Maintenance
                    </div>
                    <p className="text-slate-600 text-lg">
                        Currently under development for testing, will advise with an update when available for use again.
                    </p>
                </div>
            </div>
        );
    }
    if (view === 'roleSelection') return <LmsRoleSelection onSelectRole={openRolePortal} />;
    
    // Trainer Views
    if (view === 'trainerDashboard') {
        if (!trainerProfile) return <div>Error: Not authenticated.</div>;
        return <TrainerDashboard onNavigate={setView} onBackToRole={exitRolePortal} profile={trainerProfile} />;
    }
    if (view === 'curriculum') return <CurriculumHub onSelectCourse={(id) => { setSelectedCourseId(id); setView('courseDetail'); }} onBack={() => setView('trainerDashboard')} />;
    if (view === 'courseDetail') return <CourseDetail courseId={selectedCourseId!} lessonPlans={lessonPlans} onBack={() => setView('curriculum')} onStartModule={(modId) => { setActiveModuleId(modId); setActiveModuleData(MODULE_DATA[modId]); setView('lessonView'); }} />;
    
    // The shared lesson view used by both Trainers (preview) and Learners (active)
    if (view === 'lessonView') {
        return <OnlineLessonView 
            moduleId={activeModuleId!} 
            moduleData={activeModuleData || MODULE_DATA[activeModuleId!]}
            onBack={() => {
                setCurrentEnrolledCourse(null);
                setActiveModuleData(null);
                setView(currentLearner ? 'learnerDashboard' : 'courseDetail');
            }} 
            onComplete={(summary) => { 
                if (currentLearner && currentEnrolledCourse && props.onUpdateStudentCourse) {
                    props.onUpdateStudentCourse(currentLearner.id, currentEnrolledCourse.courseId, {
                        status: 'Completed',
                        progress: 100,
                        assessmentSummary: summary
                    });
                }
            }} 
        />;
    }

    if (view === 'studentList') return <StudentList 
        students={props.students || []} 
        organization={props.currentOrganization}
        onSelectStudent={(s) => { setSelectedStudent(s); setView('studentDetail'); }} 
        onBack={() => setView('trainerDashboard')}
    />;
    if (view === 'studentDetail') return <StudentDetail 
        student={selectedStudent!} 
        onBack={() => setView('studentList')} 
        onEnroll={handleEnrollStudent} 
        onBuildPathway={() => setView('careerPathway')}
        standardCourses={standardCourseDefinitions}
        customCourses={[...(props.customCourseDefinitions || []), ...(props.customLmsModules || []).map((m: any) => ({ id: m.id, courseName: m.title, modules: [m.title], isCustom: true }))]} 
    />;
    if (view === 'builder') return <LmsContentBuilder onBack={() => setView('trainerDashboard')} onCreateCustomModule={(m) => props.onCreateCustomModule && props.onCreateCustomModule(m)} onSaveLessonPlan={handleSaveLessonPlan} />;
    if (view === 'advisor') return <AiCompetencyAdvisor onBack={() => setView('trainerDashboard')} />;
    if (view === 'reporting') return <LmsReporting onBack={() => setView('trainerDashboard')} students={props.students || []} />;
    if (view === 'careerPathway') return <CareerPathwayBuilder 
        onBack={() => selectedStudent ? setView('studentDetail') : setView('trainerDashboard')} 
        students={props.students || []} 
        onAssignPathway={(c, sid) => props.onCreateCustomCourse && props.onCreateCustomCourse(c, sid)}
        initialStudentId={selectedStudent?.id}
    />;

    // Learner Views
     if (view === 'learnerDashboard') {
        if (!currentLearner) return <div>Not authenticated.</div>;
        return (
            <>
                {learnerResultToView && (
                     <CertificateView 
                        studentName={`${currentLearner.firstName} ${currentLearner.lastName}`} 
                        courseName={learnerResultToView.courseName} 
                        completionDate={learnerResultToView.assessmentSummary?.completionDate || new Date().toISOString()} 
                        onClose={() => setLearnerResultToView(null)} 
                        studentEmail={currentLearner.email} 
                        resultSummary={learnerResultToView.assessmentSummary} 
                        organizationName={currentLearner.organization?.trim() || (props.currentOrganization?.id === currentLearner.organizationId ? props.currentOrganization?.name : '')}
                    />
                )}
                <LearnerDashboard 
                    student={currentLearner} 
                    onSelectCourse={async (course) => {
                         const firstModule = course.modules?.[0] || course.detailedModules?.[0]?.module || '';
                         let resolved = resolveModule(firstModule, props.customLmsModules || []);
                         if (!resolved?.data && firstModule) {
                             const moduleTitle = typeof firstModule === 'object'
                                 ? String((firstModule as any).title || (firstModule as any).name || 'Assigned Module')
                                 : String(firstModule);
                             try {
                                 const generated = await generateCustomLmsModule(moduleTitle, `Prepare an interactive lesson for the assigned pathway: ${course.courseName}.`);
                                 const moduleData = { ...generated, title: generated?.title || moduleTitle };
                                 resolved = { id: generated?.id || normalizeModuleReference(moduleTitle), data: moduleData };
                             } catch (error) {
                                 console.error('Unable to prepare assigned LMS module.', error);
                             }
                         }
                         if (resolved?.data) {
                             setActiveModuleId(resolved.id);
                             setActiveModuleData(resolved.data);
                             setCurrentEnrolledCourse(course);
                             if (course.status === 'Not Started' && props.onUpdateStudentCourse)
                                 props.onUpdateStudentCourse(currentLearner.id, course.courseId, { status: 'In Progress' });
                             setView('lessonView');
                         } else {
                             void showAppAlert("Unable to prepare this module. Please try again.");
                         }
                    }} 
                    onViewResult={(course) => setLearnerResultToView(course)}
                    onLogout={exitRolePortal}
                />
            </>
        );
    }
    
    return <div>Unknown View</div>;
};

export default LearningManagementSuite;
